import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import GrayCard from "components/gray-card";
import CheckBox from "components/check-box";
import Typography from "components/typography";
import { Colors } from "utils/colors";
import DragNDrop from "components/inputs/dragNdrop";
import { Link } from "react-router-dom";
import TrackItem from "./components/track-item";
import { ITrack } from "types/musicData";

interface IProps {
  setCheckList: (value: boolean) => void;
  checkList: boolean;
  addToTrackList: (value: File) => void;
  trackList: ITrack[];
  editTrack: (value: number) => void;
  deleteTrack: (value: number) => void;
  rearrangeTrackList: (list: ITrack[]) => void;
  single: boolean;
}
const StepTwo = ({
  setCheckList,
  checkList,
  addToTrackList,
  trackList,
  editTrack,
  deleteTrack,
  rearrangeTrackList,
  single
}: IProps) => {
  const [authorized, setAuthorized] = useState(checkList);
  const [plaigarism, setPlaigarism] = useState(checkList);
  const [understanding, setUnderstanding] = useState(checkList);
  const [terms, setTerms] = useState(checkList);

  useEffect(() => {
    if (authorized && plaigarism && understanding && terms) {
      setCheckList(true);
    } else {
      setCheckList(false);
    }
  }, [authorized, plaigarism, understanding, terms]);

  //drag logic
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const dragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move"; // Optional: gives user a visual indicator
  };

  const dragEnter = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    dragOverItem.current = index;
  };

  const dragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // This prevents the entire page from being dragged
  };

  const drop = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;

    const copyListItems = [...trackList];
    const dragItemContent = copyListItems[dragItem.current];

    copyListItems.splice(dragItem.current, 1);
    copyListItems.splice(dragOverItem.current, 0, dragItemContent);

    dragItem.current = null;
    dragOverItem.current = null;

    rearrangeTrackList(copyListItems);
  };

  return (
    <Container>
      <GrayCard title="Upload Your Music">
        {trackList.map((track, i) => {
          return (
            <Track
              key={i}
              onDragStart={(e) => dragStart(e, i)}
              onDragEnter={(e) => dragEnter(e, i)}
              onDragEnd={drop}
              onDragOver={dragOver}
              draggable
            >
              <TrackItem
                number={i + 1}
                track={track}
                key={i}
                editTrack={() => editTrack(i)}
                deleteTrack={() => deleteTrack(i)}
              />
            </Track>
          );
        })}

        {trackList.length === 1 && single ? (
          <></>
        ) : (
          <FlexBox>
            <UploadMusicArea>
              <DragNDrop
                setFile={(file) => {
                  if (file) {
                    addToTrackList(file);
                  }
                }}
                type="audio"
                limit={200}
              />
            </UploadMusicArea>
            <ArtReqText>
              <Typography weight="semibold" mb="0.8" size="s">
                Audio File Requirements
              </Typography>
              <ul
                style={{
                  color: Colors.grey500,
                  padding: "0",
                  paddingLeft: "1rem",
                  margin: 0,
                  width: "70%"
                }}
              >
                <li>
                  <Typography size="xs" mb="0.4">
                    File format must be .wav or .mp3
                  </Typography>
                </li>
                <li>
                  <Typography size="xs" mb="0.4">
                    File size not larger than 200mb
                  </Typography>
                </li>
              </ul>
            </ArtReqText>
          </FlexBox>
        )}
      </GrayCard>
      <GrayCard title="Important Checklists (Mandatory)">
        <CheckItem>
          <CheckBox
            defaultState={authorized}
            onChange={(value) => setAuthorized(value)}
          />
          <Typography ml="1">
            I recorded this music, and I’m authorized to sell it in stores
            worldwide & collect royalties
          </Typography>
        </CheckItem>
        <CheckItem>
          <CheckBox
            defaultState={plaigarism}
            onChange={(value) => setPlaigarism(value)}
          />
          <Typography ml="1">
            I’m not using any other artist’s name in my name, song titles, or
            album titles without their approval
          </Typography>
        </CheckItem>
        <CheckItem>
          <CheckBox
            defaultState={understanding}
            onChange={(value) => setUnderstanding(value)}
          />
          <Typography ml="1">
            I understand that if iI don’t label tracks that contain swear words
            or obscenities correctly , my release may be rejected from stores
          </Typography>
        </CheckItem>{" "}
        <CheckItem>
          <CheckBox
            defaultState={terms}
            onChange={(value) => setTerms(value)}
          />
          <Typography ml="1">
            I have read and agreed to Rated Afrika’s{" "}
            <LinkText to={"/terms"}>Terms & Conditions</LinkText>
          </Typography>
        </CheckItem>
      </GrayCard>
    </Container>
  );
};

export default StepTwo;

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const Track = styled.div`
  position: relative;
`;

const FlexBox = styled.div`
  display: flex;
  justify-content: space-between;
  @media only screen and (max-width: 769px) {
    flex-direction: column;
    row-gap: 1.6rem;
  }
`;

const UploadMusicArea = styled.div`
  width: 60%;
  height: 8.6rem;
  @media only screen and (max-width: 769px) {
    width: 100%;
    height: 10rem;
  }
`;

const ArtReqText = styled.div`
  width: 35%;
  @media only screen and (max-width: 769px) {
    width: 100%;
  }
`;

const CheckItem = styled.div`
  display: flex;
  margin-bottom: 2.4rem;
  color: ${Colors.grey500};
  @media only screen and (max-width: 769px) {
    display: grid;
    grid-template-columns: 5% 95%;
  }
`;

const LinkText = styled(Link)`
  text-decoration: underline;
  color: ${Colors.yellow500};
`;
